import {
  colorDraftBg,
  colorGray,
  colorInactiveBg,
  colorSuccessBg,
  fontWeightStrong,
} from '@atlas-frontend/design-tokens';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import {
  CellContext,
  ColumnDef,
  ColumnFiltersState,
  createColumnHelper,
  Table as TableInstance,
  Updater,
} from '@tanstack/react-table';
import { Checkbox, Flex, Tag } from 'antd';
import { CheckboxChangeEvent } from 'antd/es/checkbox/Checkbox';
import { FormInstance } from 'antd/es/form/hooks/useForm';
import {
  FC,
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import { Padding } from '../../../common/components';
import { MultiRowTextEllipsis } from '../../../common/components/MultiRowTextEllipsis';
import { TextLink } from '../../../common/components/typography';
import {
  CloneTableRowButton,
  DeleteTableRowButton,
  getValidPeriodDisplayValueWithFormValueAttached,
  TableOperationButton,
} from '../../../common/form';

import { CREATE_PARAM } from '../../../common/queryParams/create';
import {
  ClearButton,
  DEFAULT_ROW_ESTIMATED_SIZE,
  Table,
} from '../../../common/tanstacktable';
import {
  suppressHref,
  useHrefWithQueryParams,
} from '../../../common/useHrefWithQueryParams';
import { ROW_UNIQUE_KEY_NAME } from '../../../common/utils';
import { formatDate } from '../../../common/utils/dateUtils';
import { getMapBy } from '../../../common/utils/getMapBy';
import {
  useSystemClassifierItemById,
  useSystemClassifierSelectOptions,
} from '../../../configuration';
import { useDataObjectContext } from '../../DataObjectContext';
import { DataObjectDescriptionVersionStore } from '../../DataObjectDescriptionVersionStore';
import { DataObjectOutputType } from '../../types';
import { useDataObjectFormLabelTranslation } from '../../useDataObjectFormLabelTranslation';
import { usePanelParams } from '../../usePanelParams';
import { AlgorithmAlternativeCell } from '../AlgorithmAlternativeCell';
import { AlgorithmFilter } from '../query/useGetAlgorithmsQuery';
import { AlgorithmExtendedType } from '../schema/types';
import { AlgorithmType } from '../schema/zAlgorithmType';
import {
  AlgorithmAlternativeMapping,
  alternativeColumnFilterFn,
  alternativeColumnSortingFn,
  CloneModalState,
  findAlgorithmAlternative,
  initAlgorithmAlternatives,
} from '../utils';
import translations from './AlgorithmsTable.translations.json';
import { AlgorithmsTableActions } from './AlgorithmsTableActions';
import { AlgorithmsTableFilter } from './AlgorithmsTableFilter';
import { JoinsAndMappingsPreviewModal } from './JoinsAndMappingsPreviewModal';
import { useAlgorithmTableMultilineAccessors } from './utils';
import { AlgorithmSequenceNumber } from '../AlgorithmSequenceNumber';
import * as Icon from 'react-feather';

const { addTranslations, useTranslation } = i18nNsFactory('algorithms-table');
addTranslations(translations);

const columnHelper = createColumnHelper<AlgorithmExtendedType>();

interface AlgorithmsTableViewProps {
  algorithms: AlgorithmExtendedType[];
  onCloneRow?: (algorithmId: number, state: CloneModalState) => void;
  onDeleteRow?: (id: number) => void;
  onDeleteSelectedRows?: (algorithmIds: number[]) => void;
  onTestRunSelectedRows?: (algorithms: AlgorithmExtendedType[]) => void;
  onCheckBrokenReferencesSelectedRows?: () => void;
  isLoading?: boolean;
  onChangeView?: (isAlgorithmView: boolean, isSummaryView: boolean) => void;
  filter?: AlgorithmFilter;
  onFilter?: (filterState: AlgorithmFilter) => void;
  selected: Set<number>;
  setSelected: React.Dispatch<React.SetStateAction<Set<number>>>;
}

export type AlgorithmsTableViewRef = {
  getTable: () => TableInstance<any> | null;
};

export const AlgorithmsTableView = forwardRef<
  AlgorithmsTableViewRef,
  AlgorithmsTableViewProps
>((props, ref) => {
  const {
    algorithms,
    onCloneRow,
    onDeleteRow,
    onDeleteSelectedRows,
    onTestRunSelectedRows,
    onCheckBrokenReferencesSelectedRows,
    isLoading,
    onChangeView,
    filter,
    onFilter,
    selected,
    setSelected,
  } = props;
  const { t } = useTranslation();
  const { t: tLabel } = useDataObjectFormLabelTranslation();
  const [isAlgorithmView, setIsAlgorithmView] = useState(true);
  const [isSummaryView, setIsSummaryView] = useState(true);
  const [dataObjectDescriptionVersion] =
    DataObjectDescriptionVersionStore.useValue('dataObjectDescriptionVersion');

  const alternativeMaps = useMemo(
    () =>
      initAlgorithmAlternatives(
        dataObjectDescriptionVersion.alternativeAlgorithmSets ?? [],
      ),
    [dataObjectDescriptionVersion],
  );

  const onCheckAllChange = useCallback(
    (e: CheckboxChangeEvent) => {
      let selectedRows: number[] = [];
      const checked = e.target.checked;
      if (checked) {
        selectedRows = Array.from(Array(algorithms.length).keys());
      }
      setSelected(new Set(selectedRows));
    },
    [setSelected, algorithms.length],
  );
  const onCheckChange = useCallback(
    (checked: boolean, index: number) => {
      setSelected((current) => {
        const newSelected = new Set(current);
        if (checked) {
          newSelected.add(index);
        } else {
          newSelected.delete(index);
        }
        return newSelected;
      });
    },
    [setSelected],
  );

  const getMapBySequenceAware = <
    D extends Record<PropertyKey, any>,
    K extends keyof D,
  >(
    data: D[],
    key: K,
    sequenceKey: keyof D,
  ) => {
    const map = new Map<D[K], D>();

    for (const d of data) {
      const mapKey = d[key];
      const existing = map.get(mapKey);

      if (existing) {
        if (d[sequenceKey] < existing[sequenceKey]) {
          map.set(mapKey, d);
        }
      } else {
        map.set(mapKey, d);
      }
    }

    return map;
  };

  const requiredKeysByKeys = useMemo(
    () =>
      getMapBySequenceAware(
        dataObjectDescriptionVersion.requiredKeys.reverse(),
        'key',
        'sequenceNumber',
      ),
    [dataObjectDescriptionVersion.requiredKeys],
  );

  const sourcesById = useMemo(
    () => getMapBy(algorithms.flatMap((a) => a.sources).filter(Boolean), 'id'),
    [algorithms],
  );

  const multilineAccessors = useAlgorithmTableMultilineAccessors(sourcesById);

  const [, setPanelParams] = usePanelParams();

  const columns: ColumnDef<AlgorithmExtendedType, any>[] = useMemo(() => {
    const columns: ColumnDef<AlgorithmExtendedType, any>[] = [];
    if (isAlgorithmView) {
      columns.push(
        columnHelper.display({
          id: 'selected',
          header: () => (
            <Checkbox
              id="check-all-keys"
              indeterminate={
                selected.size > 0 && selected.size < algorithms.length
              }
              checked={selected.size === algorithms.length}
              onChange={onCheckAllChange}
            />
          ),
          cell: (props) => (
            <SelectedCell
              {...props}
              selected={selected}
              onCheckChange={onCheckChange}
            />
          ),
          meta: { width: 32, className: 'selected sticky-left' },
          enableColumnFilter: false,
          enableSorting: false,
        }),
      );
    }
    columns.push(
      columnHelper.accessor('code', {
        header: tLabel(`algorithms.code.label`),
        cell: CodeCell,
        enableColumnFilter: true,
        meta: {
          width: 90,
          className: 'sticky-left',
        },
      }),
      columnHelper.accessor('typeCode', {
        header: tLabel(`algorithms.type.label`),
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'includesOption',
        meta: {
          width: 90,
        },
      }),
      columnHelper.accessor('description', {
        header: tLabel(`algorithms.description.label`),
        enableSorting: false,
        enableColumnFilter: true,
        meta: {
          width: 150,
        },
      }),
      columnHelper.accessor('groupCode', {
        header: tLabel(`algorithms.groupCode.label`),
        enableColumnFilter: true,
        meta: { width: 110 },
      }),
      columnHelper.display({
        id: 'sequenceNumber',
        header: tLabel(`algorithms.sequenceNumber.label`),
        cell: SequenceNumberCell,
        enableColumnFilter: true,
        meta: { width: 150, className: 'sequenceNumber' },
      }),
      columnHelper.accessor(
        (rowData) =>
          findAlgorithmAlternative(
            rowData.code,
            rowData.groupCode,
            alternativeMaps,
          ),
        {
          id: 'alternative',
          header: tLabel(`algorithms.alternative.label`),
          cell: AlternativeCell,
          filterFn: alternativeColumnFilterFn,
          sortingFn: alternativeColumnSortingFn,
          meta: { width: 140, className: 'alternative' },
        },
      ),
    );
    if (!isSummaryView) {
      columns.push(
        columnHelper.accessor(multilineAccessors.dimensions, {
          id: 'dimensions',
          header: tLabel(`algorithms.dimensions.label`),
          cell: (props) => (
            <MultiRowTextEllipsis>{props.renderValue()}</MultiRowTextEllipsis>
          ),
          enableSorting: false,
          enableColumnFilter: true,
          // TODO: custom filter – same dropdown dimensionCategoryIds Select
          meta: { width: 120, className: 'dimensions' },
        }),
        columnHelper.accessor(multilineAccessors.indicators, {
          id: 'indicators',
          header: tLabel(`algorithms.indicators.label`),
          cell: (props) => (
            <MultiRowTextEllipsis>{props.renderValue()}</MultiRowTextEllipsis>
          ),
          enableSorting: false,
          enableColumnFilter: true,
          meta: { width: 120, className: 'indicators' },
        }),
      );
    }
    columns.push(
      columnHelper.accessor(multilineAccessors.sources, {
        id: 'sources',
        header: tLabel(`algorithms.sources.label`),
        cell: (props) => (
          <MultiRowTextEllipsis>{props.renderValue()}</MultiRowTextEllipsis>
        ),
        enableSorting: false,
        enableColumnFilter: true,
        meta: { minWidth: 120, width: '20%', className: 'sources' },
      }),
    );

    if (isSummaryView) {
      columns.push(
        columnHelper.accessor(multilineAccessors.targetRules, {
          id: 'targetRules',
          header: tLabel(`algorithms.targetRules.label`),
          cell: (props) => (
            <MultiRowTextEllipsis>{props.renderValue()}</MultiRowTextEllipsis>
          ),
          enableSorting: false,
          enableColumnFilter: true,
          meta: { minWidth: 150, width: '20%', className: 'targetRules' },
        }),
      );
    } else {
      columns.push(
        columnHelper.accessor(multilineAccessors.mappings, {
          id: 'mappings',
          header: tLabel(`algorithms.mappings.label`),
          cell: (props) => (
            <MultiRowTextEllipsis>{props.renderValue()}</MultiRowTextEllipsis>
          ),
          enableSorting: false,
          enableColumnFilter: true,
          meta: { width: 150, className: 'mappings' },
        }),
        columnHelper.accessor(multilineAccessors.joins, {
          id: 'joins',
          header: tLabel(`algorithms.joins.label`),
          cell: (props) => (
            <MultiRowTextEllipsis>{props.renderValue()}</MultiRowTextEllipsis>
          ),
          enableSorting: false,
          enableColumnFilter: true,
          meta: { width: 150, className: 'joins' },
        }),
        columnHelper.accessor('filterClause', {
          header: tLabel(`algorithms.filterClause.label`),
          enableSorting: false,
          enableColumnFilter: true,
          meta: { width: 150 },
        }),
      );
    }
    if (isAlgorithmView) {
      columns.push(
        columnHelper.accessor((rowData) => rowData.targets?.length.toString(), {
          id: 'targetCount',
          header: tLabel(`algorithms.targetCount.label`),
          enableSorting: true,
          enableColumnFilter: true,
          meta: { width: 150, textAlign: 'right', className: 'targetCount' },
        }),
      );
    } else {
      columns.push(
        columnHelper.accessor(
          (rowData) => (rowData.targets?.[0]?.sequenceNumber ?? '').toString(),
          {
            id: 'targetSequenceNumber',
            header: tLabel(`algorithms.targetSequenceNumber.label`),
            enableSorting: true,
            enableColumnFilter: true,
            meta: { width: 150, className: 'targetSequenceNumber' },
          },
        ),
      );
    }
    if (!isSummaryView) {
      columns.push(
        columnHelper.accessor((rowData) => rowData.targets?.[0]?.condition, {
          id: 'targetCondition',
          header: tLabel(`algorithms.targetCondition.label`),
          enableSorting: false,
          enableColumnFilter: true,
          meta: { width: 150, className: 'targetCondition' },
        }),
        columnHelper.accessor((rowData) => rowData.targets?.[0]?.description, {
          id: 'targetDescription',
          header: tLabel(`algorithms.targetDescription.label`),
          enableSorting: false,
          enableColumnFilter: true,
          meta: { width: 150, className: 'targetDescription' },
        }),
      );
      if (
        dataObjectDescriptionVersion.outputTypeCode ===
        DataObjectOutputType.REPORT
      ) {
        columns.push(
          columnHelper.accessor(
            (rowData) => {
              const targetKey = rowData.targets?.[0]?.targetKey;
              if (!targetKey) {
                return '';
              }
              return `${
                requiredKeysByKeys.get(targetKey)?.sequenceNumber
              } (${targetKey})`;
            },
            {
              id: 'targetKey',
              header: tLabel(`algorithms.targetKey.label`),
              enableSorting: true,
              sortingFn: (rowA: any, rowB: any, columnId: string) => {
                const getSeqNumber = (value: string) => {
                  const seqNumber = parseInt(value.split(' ')[0]);
                  if (!isNaN(seqNumber)) {
                    return seqNumber;
                  } else {
                    return Number.MAX_SAFE_INTEGER;
                  }
                };
                const seqNumA = getSeqNumber(rowA.getValue(columnId));
                const seqNumB = getSeqNumber(rowB.getValue(columnId));
                return seqNumA - seqNumB;
              },
              enableColumnFilter: true,
              meta: { width: 150, className: 'targetKey' },
            },
          ),
        );
      }
      columns.push(
        columnHelper.accessor(multilineAccessors.targetMapRules, {
          id: 'targetMapRules',
          header: tLabel(`algorithms.targetMapRules.label`),
          cell: (props) => (
            <MultiRowTextEllipsis>{props.renderValue()}</MultiRowTextEllipsis>
          ),
          enableSorting: false,
          enableColumnFilter: true,
          meta: { width: 150, className: 'targetMapRules' },
        }),
        columnHelper.accessor(multilineAccessors.targetRuleRules, {
          id: 'targetRuleRules',
          header: tLabel(`algorithms.targetRuleRules.label`),
          cell: (props) => (
            <MultiRowTextEllipsis>{props.renderValue()}</MultiRowTextEllipsis>
          ),
          enableSorting: false,
          enableColumnFilter: true,
          meta: { width: 150, className: 'targetRuleRules' },
        }),
        columnHelper.accessor('expression', {
          header: tLabel(`algorithms.expression.label`),
          enableSorting: false,
          enableColumnFilter: true,
          meta: { width: 150 },
        }),
        columnHelper.accessor('statusName', {
          header: tLabel(`algorithms.status.label`),
          enableColumnFilter: true,
          filterFn: 'includesOption',
          meta: { width: 90 },
          cell: (props) => (
            <AlgorithmStatusLabel statusId={props.row.original.statusId} />
          ),
        }),
        columnHelper.accessor(
          (rowData) =>
            getValidPeriodDisplayValueWithFormValueAttached(
              rowData.validPeriod,
            ),
          {
            id: 'validPeriod',
            header: tLabel('algorithms.validPeriod.label'),
            cell: (props) => {
              const [validFrom, validTo] = props.row.original.validPeriod;
              if (!!validFrom || !!validTo) {
                return [validFrom, validTo]
                  .map((dateString) =>
                    dateString ? formatDate(dateString) : '...',
                  )
                  .join(' – ');
              } else {
                return '';
              }
            },
            meta: {
              width: 200,
              className: 'validPeriod',
            },
            filterFn: 'validPeriod',
          },
        ),
      );
    }

    if (props.onDeleteSelectedRows) {
      columns.push(
        columnHelper.accessor('operations' as any, {
          header: () => (
            <Flex justify="center" gap="small">
              <TableOperationButton
                id={'delete-selected-rows'}
                onClick={() => setPanelParams({ algorithmId: CREATE_PARAM })}
                title={t('button.add-algorithm')}
                icon={<Icon.Plus size={22} color={colorGray[7]} />}
                type="text"
                aria-label={t('button.add-algorithm')}
              />
            </Flex>
          ),
          enableSorting: false,
          enableColumnFilter: false,
          cell: (props) => (
            <OperationsCell
              {...props}
              onCloneRow={onCloneRow}
              onDeleteRow={onDeleteRow}
              alternativeMaps={alternativeMaps}
            />
          ),
          meta: {
            width: 88,
            className: 'sticky-right',
          },
        }),
      );
    }

    return columns;
  }, [
    t,
    tLabel,
    multilineAccessors,
    isSummaryView,
    selected,
    algorithms.length,
    onCheckAllChange,
    onCheckChange,
    alternativeMaps,
    onDeleteRow,
    isAlgorithmView,
    dataObjectDescriptionVersion.outputTypeCode,
    requiredKeysByKeys,
    setPanelParams,
    props.onDeleteSelectedRows,
    onCloneRow,
  ]);

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [headerFiltersState, setHeaderFiltersState] =
    useState<ColumnFiltersState>([]);

  const handleHeaderFiltersChange = useCallback(
    (updater: Updater<ColumnFiltersState>) => {
      setHeaderFiltersState((current) =>
        updater instanceof Function ? updater(current) : updater,
      );
    },
    [],
  );

  const filterCount = Object.values(filter || {})
    .filter(Boolean)
    .concat(headerFiltersState).length;

  const formRef = useRef<FormInstance | null>(null);
  const tableRef = useRef<TableInstance<any> | null>(null);

  useImperativeHandle(
    ref,
    () => ({
      getTable: () => tableRef.current,
    }),
    [],
  );

  const { setFilteredAlgorithmIds } = useDataObjectContext();

  // If header filters change (user applies/clears them) or if given algorithms change (they are (re)loaded from the server),
  // then update the filteredAlgorithmIds state in the context.
  // Set filtered algorithm ids regardless of any applied filters — if no filter is applied,
  // then all algorithm IDs are included (when asking to download algorithms excel).
  useEffect(() => {
    const tableInstance = tableRef.current;
    if (tableInstance) {
      setFilteredAlgorithmIds(
        tableInstance.getRowModel().rows.map((row) => row.original.id),
      );
    }
  }, [
    algorithms, // not consumed, but used to trigger the effect
    headerFiltersState, // not consumed, but used to trigger the effect
    setFilteredAlgorithmIds,
  ]);

  // Pre-calculate the estimated row size for each algorithm row based on
  // the content of their multiline cells by counting rows of text.
  //
  // Without this optimization, the table assumes that each row has the same
  // 31px height and as a result the table would still function - rows still
  // scroll and virtualize as they should - but once scrolling animation ends,
  // the rows make a little jump in Y position producing subpar UX.
  const estimateRowSize = useMemo(() => {
    const heightMap = new Map<number, number>();

    for (const [index, algorithm] of algorithms.entries()) {
      const rowHeight = Object.values(multilineAccessors).reduce(
        (height, accessorFn) => {
          const cellValue = accessorFn(algorithm, index);
          const valueRowsCount = Math.min(3, cellValue.split('\n').length);

          return Math.max(
            height,
            DEFAULT_ROW_ESTIMATED_SIZE + (valueRowsCount - 1) * 22, // 22px is regular line height for every additional line of text
          );
        },
        DEFAULT_ROW_ESTIMATED_SIZE,
      );

      heightMap.set(index, rowHeight);
    }

    return (index: number) =>
      heightMap.get(index) || DEFAULT_ROW_ESTIMATED_SIZE;
  }, [algorithms]);

  const { ALGORITHM_STATUS: algorithmStatusOptions } =
    useSystemClassifierSelectOptions(['ALGORITHM_STATUS'], 'code');
  const filterOptions = useMemo(
    () => ({
      typeCode: Object.values(AlgorithmType).map((code) => ({
        id: code,
        name: tLabel(`algorithms.type.${code}`),
      })),
      statusName: algorithmStatusOptions.map((item) => {
        const label = tLabel(`algorithms.status.${item.code}`);
        return {
          id: label,
          name: label,
        };
      }),
    }),
    [algorithmStatusOptions, tLabel],
  );

  const handleClickDeleteSelected = useMemo(() => {
    if (!onDeleteSelectedRows || !tableRef?.current?.getRowModel().rowsById) {
      return undefined;
    }

    return () => {
      const ids = Array.from(selected)
        .map((a) => tableRef?.current?.getRowModel().rowsById[a]?.original?.id)
        .filter((id): id is number => id !== undefined);

      onDeleteSelectedRows(ids);
    };
  }, [onDeleteSelectedRows, selected]);

  const handleClickTestRunSelected = useCallback(() => {
    if (!onTestRunSelectedRows || !tableRef?.current?.getRowModel().rowsById) {
      return undefined;
    }

    const algorithms = Array.from(selected)
      .map((a) => tableRef?.current?.getRowModel().rowsById[a]?.original)
      .filter(({ id }) => id !== undefined);
    onTestRunSelectedRows(algorithms);
  }, [onTestRunSelectedRows, selected]);

  const handleClickCheckBrokenReferencesSelected = useCallback(() => {
    if (
      !onCheckBrokenReferencesSelectedRows ||
      !tableRef?.current?.getRowModel().rowsById
    ) {
      return undefined;
    }

    onCheckBrokenReferencesSelectedRows();
  }, [onCheckBrokenReferencesSelectedRows]);

  const [graphicAlgorithmId, setGraphicAlgorithmId] = useState<number | null>(
    null,
  );

  return (
    <>
      <Flex vertical align="stretch" gap="middle">
        <Table
          ref={tableRef}
          defaultSort={[{ id: 'code', desc: false }]}
          actions={
            <>
              <AlgorithmsTableActions
                onChangeView={(isAlgorithmView, isSummaryView) => {
                  setIsAlgorithmView(isAlgorithmView);
                  setIsSummaryView(isSummaryView);
                  onChangeView?.(isAlgorithmView, isSummaryView);
                }}
                filter={filter}
                onFilter={onFilter}
                isFilterOpen={isFilterOpen}
                onToggleFilterOpen={() => setIsFilterOpen((prev) => !prev)}
                onClickDeleteSelected={
                  selected.size > 0 ? handleClickDeleteSelected : undefined
                }
                onClickTestRunSelected={
                  selected.size > 0 && Boolean(onTestRunSelectedRows)
                    ? handleClickTestRunSelected
                    : undefined
                }
                onClickCheckBrokenReferencesSelected={
                  selected.size > 0 &&
                  Boolean(onCheckBrokenReferencesSelectedRows)
                    ? handleClickCheckBrokenReferencesSelected
                    : undefined
                }
                onClickClearFilters={() => onFilter?.({})}
                onClickDisplayGraphically={() => {
                  const index = [...selected][0];
                  setGraphicAlgorithmId(algorithms[index].id);
                }}
                isDisplayGraphicallyDisabled={selected.size !== 1}
              />
              {Boolean(filterCount) && (
                <ClearButton
                  id="algorithm-directory-clear-filters-button"
                  count={filterCount}
                  onClick={() => {
                    onFilter?.({});
                    setHeaderFiltersState([]);
                    formRef.current?.resetFields();
                  }}
                />
              )}
            </>
          }
          hideClearFilters
          filters={headerFiltersState}
          onFilter={handleHeaderFiltersChange}
          name="algorithm-table"
          rowKey={ROW_UNIQUE_KEY_NAME}
          data={algorithms}
          columns={columns}
          options={filterOptions}
          isLoading={isLoading}
          sticky
          virtual
          estimateRowSize={estimateRowSize}
          scrollContainerSelector="#detail-layout-main-content"
        />
      </Flex>
      <AlgorithmsTableFilter
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        filter={filter}
        onFilter={onFilter}
        ref={formRef}
      />
      {graphicAlgorithmId && (
        <JoinsAndMappingsPreviewModal
          algorithmId={graphicAlgorithmId}
          onClose={() => setGraphicAlgorithmId(null)}
        />
      )}
    </>
  );
});

const SelectedCell: FC<
  CellContext<any, any> & {
    selected: Set<number>;
    onCheckChange: (checked: boolean, index: number) => void;
  }
> = (props) => (
  <Padding left={4}>
    <Checkbox
      id={'check-' + props.row.index}
      checked={props.selected.has(props.row.index)}
      onChange={(e) => props.onCheckChange(e.target.checked, props.row.index)}
    />
  </Padding>
);

const CodeCell: FC<CellContext<AlgorithmExtendedType, any>> = memo((props) => {
  const [, setPanelParams] = usePanelParams();
  const params = {
    algorithmId: props.row.original.id,
    algorithmCode: undefined,
  };

  return (
    <TextLink
      id={`algorithms-${props.row.index}-code-link`}
      href={useHrefWithQueryParams(params)}
      onClick={suppressHref(() => setPanelParams(params))}
    >
      <MultiRowTextEllipsis>{props.renderValue()}</MultiRowTextEllipsis>
    </TextLink>
  );
});

const SequenceNumberCell: FC<CellContext<any, any>> = memo((props) => {
  return (
    <AlgorithmSequenceNumber
      sequenceNumber={props.row.original.sequenceNumber}
      execAfterAggregation={props.row.original.execAfterAggregation}
    />
  );
});

const OperationsCell: FC<
  CellContext<any, any> & {
    onCloneRow?: (algorithmId: number, state: CloneModalState) => void;
    onDeleteRow?: (id: number) => void;
    alternativeMaps: AlgorithmAlternativeMapping;
  }
> = memo((props) => (
  <Flex justify="center" gap="small">
    <CloneTableRowButton
      context={props}
      onClick={() =>
        props.onCloneRow?.(props.row.original.id, CloneModalState.CLONE)
      }
    />
    <DeleteTableRowButton
      id={`delete-table-row-${props.row.index}`}
      confirm={{
        onConfirm: () => props.onDeleteRow?.(props.row.original.id),
      }}
      disabled={
        !!findAlgorithmAlternative(
          props.row.original.code,
          props.row.original.groupCode,
          props.alternativeMaps,
        )
      }
    />
  </Flex>
));

const AlternativeCell: FC<CellContext<any, any>> = memo((props) => (
  <AlternativeCellWrapper justify="center">
    <AlgorithmAlternativeCell alternativeSet={props.renderValue()} />
  </AlternativeCellWrapper>
));

const AlternativeCellWrapper = styled(Flex)`
  width: 100%;
`;

const AlgorithmStatusLabelTag = styled(Tag)`
  && {
    align-items: center;
    font-size: 11px;
    font-weight: ${fontWeightStrong};
    letter-spacing: 0.1em;
    height: 24px;
    color: ${colorGray[8]};
  }
`;

const AlgorithmStatusLabel = memo((props: { statusId: number }) => {
  const status = useSystemClassifierItemById(
    'ALGORITHM_STATUS',
    props.statusId,
  );
  let color;
  switch (status?.code) {
    case 'ACTIVE':
      color = colorSuccessBg;
      break;
    case 'INACTIVE':
      color = colorInactiveBg;
      break;
    default:
      color = colorDraftBg;
      break;
  }
  return (
    <AlgorithmStatusLabelTag color={color}>
      {status?.name}
    </AlgorithmStatusLabelTag>
  );
});
